<template>
  <div class="quality-page dark-panel">
    <section class="quality-hero reveal">
      <div class="quality-hero__bg">
        <img :src="page.hero.image" alt="">
      </div>
      <div class="quality-hero__container container">
        <div class="quality-hero__content">
          <p class="quality-hero__pretitle">{{ page.hero.pretitle }}</p>
          <h1 class="quality-hero__title">
            {{ page.hero.title }}
            <span>{{ page.hero.titleBold }}</span>
          </h1>
        </div>
      </div>
    </section>

    <section class="quality-intro reveal">
      <div class="container container-narrow">
        <p
          v-for="(paragraph, index) in page.introParagraphs"
          :key="index"
          :class="{ 'is-strong': index === 1 }"
        >
          <strong v-if="index === 1">{{ paragraph }}</strong>
          <template v-else>{{ paragraph }}</template>
        </p>
      </div>
    </section>

    <section class="quality-teasers reveal">
      <div class="container container-medium">
        <div class="quality-teasers__grid quality-teasers__grid--row1">
          <a
            v-for="item in page.teasersRow1"
            :key="item.label + item.anchor"
            class="quality-teaser"
            :href="'#' + item.anchor"
            @click.prevent="scrollTo(item.anchor)"
          >
            <div class="quality-teaser__image ratio-1x1">
              <img :src="item.image" :alt="item.label">
            </div>
            <div class="quality-teaser__label">
              <span>{{ item.label }}</span>
            </div>
          </a>
        </div>
        <div class="quality-teasers__grid quality-teasers__grid--row2">
          <a
            v-for="(item, index) in page.teasersRow2"
            :key="item.label + '-' + index"
            class="quality-teaser"
            :href="'#' + item.anchor"
            @click.prevent="scrollTo(item.anchor)"
          >
            <div class="quality-teaser__image ratio-1x1">
              <img :src="item.image" :alt="item.label">
            </div>
            <div class="quality-teaser__label">
              <span>{{ item.label }}</span>
            </div>
          </a>
        </div>
      </div>
    </section>

    <section :id="page.origin.id" class="quality-origin quality-anchor reveal">
      <div class="container">
        <div class="quality-origin__grid">
          <div class="quality-origin__text">
            <div class="quality-origin__head">
              <h2 class="quality-section-title">{{ page.origin.title }}</h2>
            </div>
            <div class="quality-origin__body">
              <p v-for="paragraph in page.origin.paragraphs" :key="paragraph">{{ paragraph }}</p>
            </div>
          </div>
          <div class="quality-origin__media quality-origin__media--lg ratio-4x3">
            <img :src="page.origin.images.large" alt="">
          </div>
          <div class="quality-origin__media quality-origin__media--portrait ratio-3x4">
            <img :src="page.origin.images.portrait" alt="">
          </div>
          <div class="quality-origin__media quality-origin__media--sm ratio-4x3">
            <img :src="page.origin.images.small" alt="">
          </div>
        </div>
      </div>
    </section>

    <section :id="page.ingredients.id" class="quality-split quality-anchor reveal">
      <div class="container">
        <div class="quality-split__box quality-split__box--right">
          <div class="quality-split__copy">
            <p class="quality-split__number">{{ page.ingredients.number }}</p>
            <h2 class="quality-section-title">{{ page.ingredients.title }}</h2>
            <p v-for="paragraph in page.ingredients.paragraphs" :key="paragraph">{{ paragraph }}</p>
          </div>
          <div class="quality-split__media ratio-4x3">
            <img :src="page.ingredients.image" alt="">
          </div>
        </div>
      </div>
    </section>

    <section :id="page.surface.id" class="quality-zoom quality-anchor reveal">
      <div class="container">
        <div class="quality-zoom__layout quality-zoom__layout--reverse">
          <div class="quality-zoom__media ratio-4x3">
            <img :src="page.surface.image" alt="">
          </div>
          <div class="quality-zoom__copy">
            <p class="quality-split__number">{{ page.surface.number }}</p>
            <h2 class="quality-section-title">{{ page.surface.title }}</h2>
            <p v-for="paragraph in page.surface.paragraphs" :key="paragraph">{{ paragraph }}</p>
          </div>
        </div>
      </div>
    </section>

    <section :id="page.effect.id" class="quality-effect quality-anchor reveal">
      <div class="container">
        <div class="quality-effect__layout">
          <header class="quality-effect__heading">
            <p class="quality-split__number">{{ page.effect.number }}</p>
            <h2 class="quality-effect__title">{{ page.effect.title }}</h2>
          </header>
          <div class="quality-effect__slider">
            <article
              v-for="item in page.effect.items"
              :key="item.title"
              class="quality-effect-card"
            >
              <div class="quality-effect-card__image ratio-3x4">
                <img :src="item.image" :alt="item.title">
              </div>
              <div class="quality-effect-card__body">
                <p class="quality-effect-card__subtitle">{{ item.subtitle }}</p>
                <h3>{{ item.title }}</h3>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section :id="page.structure.id" class="quality-split quality-anchor reveal">
      <div class="container">
        <div class="quality-split__box quality-split__box--right">
          <div class="quality-split__copy">
            <p class="quality-split__number">{{ page.structure.number }}</p>
            <h2 class="quality-section-title">{{ page.structure.title }}</h2>
            <p v-for="paragraph in page.structure.paragraphs" :key="paragraph">{{ paragraph }}</p>
          </div>
          <div class="quality-split__media ratio-4x3">
            <img :src="page.structure.image" alt="">
          </div>
        </div>
      </div>
    </section>

    <section class="quality-note reveal">
      <div class="container container-narrow quality-note__inner">
        <p>{{ page.structure.note }}</p>
      </div>
    </section>

    <section :id="page.installation.id" class="quality-split quality-anchor reveal">
      <div class="container">
        <div class="quality-split__box quality-split__box--left">
          <div class="quality-split__copy">
            <p class="quality-split__number">{{ page.installation.number }}</p>
            <h2 class="quality-section-title">{{ page.installation.title }}</h2>
            <p>{{ page.installation.paragraphs[0] }}</p>
            <ul>
              <li v-for="item in page.installation.list" :key="item">{{ item }}</li>
            </ul>
          </div>
          <div class="quality-split__media ratio-4x3">
            <img :src="page.installation.image" alt="">
          </div>
        </div>
      </div>
    </section>

    <section :id="page.color.id" class="quality-narrow quality-anchor reveal">
      <div class="container container-narrow">
        <p class="quality-split__number">{{ page.color.number }}</p>
        <h2 class="quality-section-title">{{ page.color.title }}</h2>
        <p v-for="paragraph in page.color.paragraphs" :key="paragraph">{{ paragraph }}</p>
        <!-- <router-link class="quality-btn" :to="page.color.cta.to">{{ page.color.cta.label }}</router-link> -->
      </div>
    </section>

    <section class="quality-gallery reveal">
      <div class="container">
        <div class="quality-mosaic quality-mosaic--a">
          <div
            v-for="(image, index) in page.color.galleryRow1"
            :key="'mosaic-a-' + index"
            class="quality-mosaic__cell"
          >
            <img :src="image" alt="">
          </div>
        </div>
        <div class="quality-mosaic quality-mosaic--b">
          <div
            v-for="(image, index) in page.color.galleryRow2"
            :key="'mosaic-b-' + index"
            class="quality-mosaic__cell"
          >
            <img :src="image" alt="">
          </div>
        </div>
      </div>
    </section>

    <section :id="page.formats.id" class="quality-narrow quality-anchor reveal">
      <div class="container container-narrow">
        <p class="quality-split__number">{{ page.formats.number }}</p>
        <h2 class="quality-section-title">{{ page.formats.title }}</h2>
        <p v-for="paragraph in page.formats.paragraphs" :key="paragraph">{{ paragraph }}</p>
        <router-link class="quality-btn" :to="page.formats.cta.to">{{ page.formats.cta.label }}</router-link>
      </div>
    </section>

    <section class="quality-formats reveal">
      <div class="container">
        <div class="quality-formats__row">
          <router-link
            v-for="item in page.formats.products"
            :key="item.title"
            class="quality-format-card"
            :to="item.to"
          >
            <div class="quality-format-card__image ratio-3x4">
              <img :src="item.image" :alt="item.title">
            </div>
            <h3>{{ item.title }}</h3>
          </router-link>
        </div>
      </div>
    </section>

    <section :id="page.care.id" class="quality-zoom quality-anchor reveal">
      <div class="container">
        <div class="quality-zoom__layout">
          <div class="quality-zoom__media ratio-4x3">
            <img :src="page.care.image" alt="">
          </div>
          <div class="quality-zoom__copy quality-zoom__copy--offset">
            <p class="quality-split__number">{{ page.care.number }}</p>
            <h2 class="quality-section-title">{{ page.care.title }}</h2>
            <p v-for="paragraph in page.care.paragraphs" :key="paragraph">{{ paragraph }}</p>
            <router-link class="quality-btn" :to="page.care.cta.to">{{ page.care.cta.label }}</router-link>
          </div>
        </div>
      </div>
    </section>

    <section :id="page.disposal.id" class="quality-split quality-disposal quality-anchor reveal">
      <div class="container">
        <div class="quality-split__box quality-split__box--left">
          <div class="quality-split__copy">
            <p class="quality-split__number">{{ page.disposal.number }}</p>
            <h2 class="quality-section-title">{{ page.disposal.title }}</h2>
            <p v-for="paragraph in page.disposal.paragraphs" :key="paragraph">{{ paragraph }}</p>
          </div>
          <div class="quality-split__media ratio-4x3">
            <img :src="page.disposal.image" alt="">
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import site from '../mock/strongova'
import { fetchPublicPageSections, resolveAssetUrl } from '../api/public'

function clonePage () {
  return JSON.parse(JSON.stringify(site.qualityPage))
}

function descriptionLines (section) {
  if (!section || !section.description) return []
  return section.description
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
}

function sectionMap (sections) {
  return sections.reduce((map, item) => {
    map[item.sectionKey] = item
    return map
  }, {})
}

export default {
  name: 'QualityPage',
  data () {
    return {
      page: clonePage()
    }
  },
  created () {
    fetchPublicPageSections({ pageKey: 'quality' })
      .then(sections => {
        if (Array.isArray(sections) && sections.length) {
          this.applyPageSections(sections)
        }
      })
      .catch(() => {
        this.page = clonePage()
      })
  },
  methods: {
    applyTextSection (target, section) {
      if (!section) return
      target.title = section.title || target.title
      const paragraphs = descriptionLines(section)
      if (paragraphs.length) target.paragraphs = paragraphs
      if (section.image && target.image) {
        target.image = resolveAssetUrl(section.image, target.image)
      }
    },
    applyTeasersSection (page, section) {
      if (!section || !Array.isArray(section.imagesJson) || !section.imagesJson.length) return
      const teasers = section.imagesJson
        .map((item, index) => ({
          label: item.title || item.label || '',
          image: resolveAssetUrl(item.image),
          anchor: item.anchor || '',
          row: Number(item.row) || (index < 6 ? 1 : 2)
        }))
        .filter(item => item.label && item.image && item.anchor)

      if (!teasers.length) return
      page.teasersRow1 = teasers.filter(item => item.row === 1)
      page.teasersRow2 = teasers.filter(item => item.row === 2)
    },
    applyOriginSection (page, section) {
      if (!section) return
      page.origin.title = section.title || page.origin.title
      const paragraphs = descriptionLines(section)
      if (paragraphs.length) page.origin.paragraphs = paragraphs

      if (Array.isArray(section.imagesJson) && section.imagesJson.length) {
        const imagesByRole = section.imagesJson.reduce((map, item) => {
          const role = String(item.title || item.role || '').toLowerCase()
          if (role && item.image) map[role] = resolveAssetUrl(item.image)
          return map
        }, {})

        page.origin.images.large = imagesByRole.large || resolveAssetUrl(section.imagesJson[0].image, page.origin.images.large)
        page.origin.images.portrait = imagesByRole.portrait || resolveAssetUrl(section.imagesJson[1] && section.imagesJson[1].image, page.origin.images.portrait)
        page.origin.images.small = imagesByRole.small || resolveAssetUrl(section.imagesJson[2] && section.imagesJson[2].image, page.origin.images.small)
      }
    },
    applyEffectSection (page, section) {
      if (!section) return
      page.effect.title = section.title || page.effect.title
      if (Array.isArray(section.imagesJson) && section.imagesJson.length) {
        page.effect.items = section.imagesJson
          .map((item, index) => {
            const fallback = page.effect.items[index] || page.effect.items[0] || {}
            return {
              title: item.title || fallback.title || '',
              subtitle: item.subtitle || item.description || fallback.subtitle || '',
              image: resolveAssetUrl(item.image, fallback.image)
            }
          })
          .filter(item => item.title && item.image)
      }
    },
    applyStructureSection (page, section) {
      this.applyTextSection(page.structure, section)
      if (!section || !Array.isArray(section.imagesJson)) return
      const note = section.imagesJson.find(item => String(item.title || item.role || '').toLowerCase() === 'note')
      if (note) {
        page.structure.note = note.description || note.text || page.structure.note
      }
    },
    applyInstallationSection (page, section) {
      this.applyTextSection(page.installation, section)
      if (!section || !Array.isArray(section.imagesJson) || !section.imagesJson.length) return
      page.installation.list = section.imagesJson
        .map(item => item.title || item.description)
        .filter(Boolean)
    },
    applyPageSections (sections) {
      const page = clonePage()
      const mappedSections = sectionMap(sections)
      const hero = mappedSections.hero
      const intro = mappedSections.intro
      const color = mappedSections.gallery
      const formats = mappedSections.content
      const care = mappedSections.care
      const disposal = mappedSections.disposal

      if (hero) {
        page.hero.title = hero.title || page.hero.title
        page.hero.pretitle = hero.description || page.hero.pretitle
        page.hero.image = resolveAssetUrl(hero.image, page.hero.image)
      }

      if (intro && intro.description) {
        page.introParagraphs = descriptionLines(intro)
      }

      this.applyTeasersSection(page, mappedSections.teasers)
      this.applyOriginSection(page, mappedSections.origin)
      this.applyTextSection(page.ingredients, mappedSections.ingredients)
      this.applyTextSection(page.surface, mappedSections.surface)
      this.applyEffectSection(page, mappedSections.effect)
      this.applyStructureSection(page, mappedSections.structure)
      this.applyInstallationSection(page, mappedSections.installation)

      if (color) {
        page.color.title = color.title || page.color.title
        const paragraphs = descriptionLines(color)
        if (paragraphs.length) page.color.paragraphs = paragraphs
        if (Array.isArray(color.imagesJson) && color.imagesJson.length) {
          const images = color.imagesJson.map(item => resolveAssetUrl(item.image)).filter(Boolean)
          page.color.galleryRow1 = images.slice(0, Math.ceil(images.length / 2))
          page.color.galleryRow2 = images.slice(Math.ceil(images.length / 2))
        }
      }

      if (formats) {
        page.formats.title = formats.title || page.formats.title
        const paragraphs = descriptionLines(formats)
        if (paragraphs.length) page.formats.paragraphs = paragraphs
        if (Array.isArray(formats.imagesJson) && formats.imagesJson.length) {
          page.formats.products = formats.imagesJson
            .map((item, index) => {
              const fallback = page.formats.products[index] || page.formats.products[0] || {}
              return {
                title: item.title || fallback.title || '',
                image: resolveAssetUrl(item.image, fallback.image),
                to: item.to || fallback.to || '/products'
              }
            })
            .filter(item => item.image)
        }
      }

      this.applyTextSection(page.care, care)
      this.applyTextSection(page.disposal, disposal)
      this.page = page
    },
    scrollTo (id) {
      const node = document.getElementById(id)
      if (!node) return
      const top = node.getBoundingClientRect().top + window.scrollY - 88
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }
}
</script>

<style>
.quality-page {
  color: #fff;
  background: linear-gradient(180deg, #090909 0%, #000 100%);
}

.quality-anchor {
  scroll-margin-top: 96px;
}

.quality-page .container-narrow {
  width: min(70.4375rem, calc(100vw - 48px));
  margin: 0 auto;
}

.quality-page .container-medium {
  width: min(87.375rem, calc(100vw - 48px));
  margin: 0 auto;
}

.quality-page .container--move-right {
  width: min(calc(100vw - 48px), 100%);
  margin-left: auto;
  margin-right: 0;
  padding-right: 35px;
  padding-left: 24px;
}

.ratio-1x1 { aspect-ratio: 1 / 1; }
.ratio-3x4 { aspect-ratio: 3 / 4; }
.ratio-4x3 { aspect-ratio: 4 / 3; }

.quality-page img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.quality-hero {
  position: relative;
  margin-bottom: clamp(6rem, 10vw, 12rem);
}

.quality-hero__bg {
  position: absolute;
  inset: 0 0 auto;
  height: min(72vh, 720px);
  z-index: 0;
}

.quality-hero__bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.58) 52%, #000 100%);
}

.quality-hero__bg img {
  height: 100%;
  object-fit: cover;
}

.quality-hero__container {
  position: relative;
  z-index: 1;
  padding-top: 13.625rem;
}

.quality-hero__content {
  position: relative;
  text-align: center;
  color: #f5f3ef;
}

.quality-hero__content::before {
  content: "";
  position: absolute;
  top: -1.875rem;
  left: 50%;
  width: 100%;
  max-width: 85%;
  height: calc(100% + 3.75rem);
  max-height: 30.5rem;
  border: 1px solid rgba(255, 255, 255, 0.28);
  transform: translateX(-50%);
  z-index: -1;
}

.quality-hero__pretitle {
  margin: 0 0 0.9375rem;
  font-size: 0.875rem;
  letter-spacing: 0.12em;
  line-height: 1.375rem;
  text-transform: uppercase;
  color: rgba(245, 243, 239, 0.72);
}

.quality-hero__title {
  margin: 0;
  padding: 0 0.9375rem;
  font-size: clamp(2.2rem, 4vw, 3.6rem);
  font-weight: 400;
  line-height: 1.12;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.quality-hero__title span {
  font-weight: 700;
}

.quality-intro {
  margin-top: clamp(2.5rem, 4vw, 3.5rem);
  padding-bottom: 0.5rem;
}

.quality-teasers {
  margin-top: clamp(2.5rem, 4vw, 4rem);
  padding-bottom: 0.5rem;
}

.quality-origin,
.quality-split,
.quality-zoom,
.quality-effect,
.quality-note,
.quality-narrow,
.quality-gallery,
.quality-formats {
  margin-top: clamp(3rem, 5vw, 4.5rem);
}

.quality-intro p {
  margin: 0 0 1.25rem;
  font-size: 16px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.82);
}

.quality-intro p.is-strong {
  margin: 0 0 1.25rem;
  color: #fff;
}

.quality-intro p.is-strong strong {
  font-weight: 700;
}

.quality-teasers__grid {
  display: grid;
  gap: 12px;
  justify-content: center;
}

.quality-teasers__grid--row1 {
  grid-template-columns: repeat(6, minmax(0, 1fr));
  max-width: 1080px;
  margin: 0 auto;
}

.quality-teasers__grid--row2 {
  grid-template-columns: repeat(5, minmax(0, 1fr));
  max-width: 900px;
  margin: 1rem auto 0;
}

.quality-teaser {
  position: relative;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}

.quality-teaser__image {
  overflow: hidden;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.quality-teaser__label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.3125rem;
  background: rgba(0, 0, 0, 0);
  color: #fff;
  opacity: 0;
  transition: opacity 0.2s ease, background 0.2s ease;
}

.quality-teaser__label span {
  font-size: 1.125rem;
  letter-spacing: 0.08em;
  line-height: 1.2;
  text-transform: uppercase;
  transform: translateY(10%);
  transition: transform 0.2s ease;
}

.quality-teaser:hover .quality-teaser__label {
  opacity: 0.9;
  background: rgba(0, 0, 0, 0.72);
}

.quality-teaser:hover .quality-teaser__label span {
  transform: translateY(0);
}

.quality-section-title {
  margin: 0 0 16px;
  font-size: clamp(28px, 3vw, 48px);
  font-weight: 400;
  line-height: 1.1;
  color: #fff;
}

.quality-split__number {
  margin: 0 0 10px;
  font-size: 15px;
  letter-spacing: 0.1em;
  text-transform: lowercase;
  color: rgba(255, 255, 255, 0.55);
}

.quality-origin__grid {
  display: grid;
  grid-template-areas:
    "media"
    "text"
    "small"
    "portrait";
  gap: 1.25rem;
  position: relative;
  align-items: start;
}

.quality-origin__grid::before {
  content: "";
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-left: none;
  border-top: none;
  pointer-events: none;
  left: -1.5rem;
  right: 1.875rem;
  top: 58%;
  bottom: 10%;
}

.quality-origin__media {
  overflow: hidden;
  min-width: 0;
  background: #111;
  border-radius: 12px;
  box-shadow: 0 0 32px rgba(0, 0, 0, 0.45);
}

.quality-origin__media--lg {
  grid-area: media;
  width: 100%;
}

.quality-origin__text {
  grid-area: text;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.quality-origin__head {
  position: relative;
  z-index: 1;
}

.quality-origin__head .quality-section-title {
  margin-bottom: 0;
}

.quality-origin__body p {
  margin: 0 0 1rem;
  font-size: 16px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.82);
}

.quality-origin__body p:last-child {
  margin-bottom: 0;
}

.quality-origin__media--portrait {
  grid-area: portrait;
  width: 100%;
  max-width: 78%;
  justify-self: start;
}

.quality-origin__media--sm {
  grid-area: small;
  width: 74%;
  justify-self: end;
}

.quality-split__box {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: center;
}

.quality-split__box::before {
  content: "";
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.32);
  pointer-events: none;
  top: 5rem;
  left: -1rem;
  width: calc(100% + 32px);
  height: calc(100% - 5rem);
}

.quality-split__box--right::before {
  right: 0;
  left: auto;
}

.quality-split__copy {
  position: relative;
  z-index: 1;
  padding: 2.5rem 0;
}

.quality-split__copy p,
.quality-split__copy li {
  margin: 0 0 1rem;
  font-size: 16px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.82);
}

.quality-split__copy ul {
  margin: 0;
  padding-left: 1.2rem;
}

.quality-split__media {
  overflow: hidden;
  background: #111;
  border-radius: 12px;
}

.quality-zoom__layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: end;
}

.quality-zoom__copy p {
  margin: 0 0 1rem;
  font-size: 16px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.82);
}

.quality-zoom__media {
  overflow: hidden;
  background: #111;
  border-radius: 12px;
}

.quality-effect__layout {
  display: flex;
  flex-direction: column;
  gap: clamp(1.5rem, 3vw, 2.5rem);
}

.quality-effect__heading {
  max-width: 52rem;
}

.quality-effect__title {
  margin: 0;
  font-size: clamp(1.75rem, 2.8vw, 2.75rem);
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: 0.02em;
  color: #fff;
}

.quality-effect__slider {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(12px, 2vw, 20px);
  width: 100%;
}

.quality-effect-card {
  min-width: 0;
}

.quality-effect-card__image {
  overflow: hidden;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.quality-effect-card__body {
  padding-top: 0.9rem;
  text-align: center;
}

.quality-effect-card__subtitle {
  margin: 0 0 0.5rem;
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
}

.quality-effect-card h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.08em;
  color: #fff;
}

.quality-note {
  margin-top: 1.5rem;
}

.quality-note__inner {
  padding: 1.5rem 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.quality-note p {
  margin: 0;
  font-size: 16px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.82);
}

.quality-narrow p {
  margin: 0 0 1rem;
  font-size: 16px;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.82);
}

.quality-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  margin-top: 1.25rem;
  padding: 0 1.4rem;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.03);
  color: #fff;
  text-decoration: none;
  letter-spacing: 0.03em;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.quality-btn:hover {
  background: #fff;
  border-color: #fff;
  color: #000;
}

.quality-gallery {
  margin-top: 2.5rem;
}

.quality-mosaic {
  display: grid;
  gap: 10px;
}

.quality-mosaic + .quality-mosaic {
  margin-top: 10px;
}

/* 上行：4 小图 + 右侧 1 大图 */
.quality-mosaic--a {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-areas:
    "s1 s2"
    "s3 s4"
    "big big";
}

.quality-mosaic--a .quality-mosaic__cell:nth-child(1) { grid-area: s1; }
.quality-mosaic--a .quality-mosaic__cell:nth-child(2) { grid-area: s2; }
.quality-mosaic--a .quality-mosaic__cell:nth-child(3) { grid-area: s3; }
.quality-mosaic--a .quality-mosaic__cell:nth-child(4) { grid-area: s4; }
.quality-mosaic--a .quality-mosaic__cell:nth-child(5) { grid-area: big; }

/* 下行：左侧 1 大图 + 4 小图 */
.quality-mosaic--b {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-areas:
    "big big"
    "s1 s2"
    "s3 s4";
}

.quality-mosaic--b .quality-mosaic__cell:nth-child(1) { grid-area: big; }
.quality-mosaic--b .quality-mosaic__cell:nth-child(2) { grid-area: s1; }
.quality-mosaic--b .quality-mosaic__cell:nth-child(3) { grid-area: s2; }
.quality-mosaic--b .quality-mosaic__cell:nth-child(4) { grid-area: s3; }
.quality-mosaic--b .quality-mosaic__cell:nth-child(5) { grid-area: s4; }

.quality-mosaic__cell {
  position: relative;
  overflow: hidden;
  min-height: 0;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.quality-mosaic--a .quality-mosaic__cell:nth-child(-n+4),
.quality-mosaic--b .quality-mosaic__cell:nth-child(n+2) {
  aspect-ratio: 1 / 1;
}

.quality-mosaic--a .quality-mosaic__cell:nth-child(5),
.quality-mosaic--b .quality-mosaic__cell:nth-child(1) {
  aspect-ratio: 2 / 1;
  min-height: 200px;
}

.quality-formats__row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.quality-format-card {
  min-width: 0;
  color: inherit;
  text-decoration: none;
  transition: transform 0.24s ease;
}

.quality-format-card__image {
  overflow: hidden;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.quality-format-card h3 {
  margin: 1rem 0 0;
  padding: 0 4px;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.26;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.04em;
}

.quality-format-card:hover {
  transform: translateY(-3px);
}

.quality-format-card:hover h3 {
  color: #fff;
}

.quality-effect__slider::-webkit-scrollbar {
  height: 4px;
}

.quality-effect__slider::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.25);
}

.quality-formats__row::-webkit-scrollbar {
  height: 4px;
}

.quality-formats__row::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.25);
}

.quality-disposal {
  padding-bottom: clamp(80px, 9vw, 120px);
}

@media (min-width: 768px) {
  .quality-hero__bg {
    height: min(88vh, 900px);
  }

  .quality-hero__container {
    padding-top: clamp(14rem, 18vw, 20.625rem);
  }

  .quality-hero__content::before {
    top: -3.125rem;
    max-width: 66.25rem;
    height: calc(100% + 6.25rem);
    border-color: rgba(255, 255, 255, 0.38);
  }

  .quality-hero__pretitle {
    font-size: 1rem;
    letter-spacing: 0.125rem;
    line-height: 1.25rem;
    margin-bottom: 0;
  }

  .quality-hero__title {
    font-size: clamp(4.5rem, 8vw, 9.0625rem);
    letter-spacing: 1.9375rem;
    line-height: 1.1;
    margin-left: 0;
  }

  .quality-origin__grid {
    grid-template-areas:
      "media text"
      "media text"
      "portrait small";
    grid-template-columns: 53% 35%;
    column-gap: 0;
    justify-content: space-between;
    padding-right: 8%;
  }

  .quality-origin__grid::before {
    left: 30%;
    right: 0;
    top: 6.25rem;
    bottom: 30%;
    border-color: rgba(255, 255, 255, 0.3);
  }

  .quality-origin__head {
    margin-left: -55%;
    padding-top: 9.625rem;
  }

  .quality-origin__body {
    margin-top: 0.5rem;
    padding-right: 0;
  }

  .quality-zoom__layout--reverse .quality-zoom__copy {
    order: 1;
  }

  .quality-zoom__layout--reverse .quality-zoom__media {
    order: 2;
  }

  .quality-origin__media--portrait {
    max-width: 23.6875rem;
    width: 100%;
    padding-top: 3.75rem;
    justify-self: start;
  }

  .quality-origin__media--sm {
    width: 100%;
    justify-self: end;
  }

  .quality-split__box {
    grid-template-columns: 5fr 6fr;
    gap: 2rem;
    padding-top: 5rem;
    padding-bottom: 5rem;
  }

  .quality-split__box--right {
    padding-right: 8.333%;
  }

  .quality-split__box--right .quality-split__copy {
    order: 1;
    padding-left: 8.333%;
  }

  .quality-split__box--right .quality-split__media {
    order: 2;
  }

  .quality-split__box--left {
    padding-left: 8.333%;
  }

  .quality-split__box--left .quality-split__copy {
    order: 2;
    padding-left: 4.333%;
  }

  .quality-split__box--left .quality-split__media {
    order: 1;
  }

  .quality-split__box::before {
    top: 0;
    height: 100%;
    width: 83.333%;
  }

  .quality-zoom__layout {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 3rem;
  }

  .quality-zoom__layout--reverse .quality-zoom__media {
    order: 2;
  }

  .quality-zoom__copy--offset {
    padding-left: 2rem;
    padding-bottom: 3rem;
  }

  .quality-mosaic--a {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-rows: repeat(2, minmax(180px, auto));
    grid-template-areas:
      "s1 s2 big big"
      "s3 s4 big big";
  }

  .quality-mosaic--b {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-rows: repeat(2, minmax(180px, auto));
    grid-template-areas:
      "big big s1 s2"
      "big big s3 s4";
  }

  .quality-mosaic--a .quality-mosaic__cell:nth-child(-n+4),
  .quality-mosaic--b .quality-mosaic__cell:nth-child(n+2) {
    aspect-ratio: auto;
    min-height: 180px;
  }

  .quality-mosaic--a .quality-mosaic__cell:nth-child(5),
  .quality-mosaic--b .quality-mosaic__cell:nth-child(1) {
    aspect-ratio: auto;
    min-height: 100%;
  }

  .quality-formats__row {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 1200px) and (min-width: 769px) {
  .quality-origin__head {
    margin-left: -42%;
    padding-top: 7rem;
  }

  .quality-origin__grid {
    grid-template-columns: 50% 42%;
    padding-right: 4%;
  }
}

@media (max-width: 1024px) {
  .quality-teasers__grid--row1 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .quality-teasers__grid--row2 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .quality-effect__slider {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }

  .quality-origin__head {
    margin-left: 0;
    padding-top: 0;
  }

  .quality-origin__grid::before {
    display: none;
  }

  .quality-origin__grid {
    grid-template-areas:
      "media"
      "text"
      "small"
      "portrait";
    grid-template-columns: 1fr;
    padding-right: 0;
  }

  .quality-origin__media--portrait {
    max-width: 78%;
    width: 100%;
    padding-top: 0;
    justify-self: start;
  }

  .quality-origin__media--sm {
    width: 74%;
    justify-self: end;
  }

  .quality-split__box {
    padding-top: 2.5rem;
    padding-bottom: 2.5rem;
  }

  .quality-split__box .quality-split__media {
    order: -1;
  }

  .quality-split__box .quality-split__copy {
    order: 0;
  }

  .quality-zoom__layout {
    grid-template-columns: 1fr;
  }

  .quality-zoom__layout .quality-zoom__media {
    order: -1;
  }

  .quality-zoom__layout .quality-zoom__copy {
    order: 0;
  }

  .quality-mosaic--a,
  .quality-mosaic--b {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: auto;
  }

  .quality-mosaic--a {
    grid-template-areas:
      "s1 s2"
      "s3 s4"
      "big big";
  }

  .quality-mosaic--b {
    grid-template-areas:
      "big big"
      "s1 s2"
      "s3 s4";
  }
}

@media (max-width: 767px) {
  .quality-page .container--move-right {
    padding-right: 1.5625rem;
    padding-left: 1.5625rem;
  }

  .quality-effect__slider {
    display: flex;
    gap: 1rem;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    scroll-snap-type: x mandatory;
  }

  .quality-effect-card {
    flex: 0 0 min(260px, 78vw);
    scroll-snap-align: start;
  }

  .quality-teasers__grid--row1,
  .quality-teasers__grid--row2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-width: none;
  }

  .quality-formats__row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .quality-teaser__label span {
    font-size: 0.875rem;
  }

  .quality-hero {
    margin-bottom: 4.5rem;
  }

  .quality-hero__bg {
    height: 52vh;
    min-height: 280px;
  }

  .quality-hero__container {
    padding-top: 11rem;
  }

  .quality-hero__title {
    letter-spacing: 0.18rem;
    font-size: 2rem;
    line-height: 1.2;
  }

  .quality-intro p,
  .quality-origin__body p,
  .quality-split__copy p,
  .quality-split__copy li,
  .quality-zoom__copy p,
  .quality-note p,
  .quality-narrow p {
    font-size: 14px;
    line-height: 2;
    color: rgba(255, 255, 255, 0.8);
  }

  .quality-split__box {
    padding: 24px 0;
  }

  .quality-split__box::before {
    width: 100%;
    left: 0;
    right: 0;
    border-color: rgba(255, 255, 255, 0.26);
  }

  .quality-section-title {
    font-size: 28px;
  }
}
</style>
